import { useFacts } from '@renderer/hooks/useFacts.ts'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer.ts'
import { ActionsProvider, AuthProvider, ModelProvider, ReactQueryProvider } from '@renderer/chat/providers'
import { useSetLastFocusedWindow, useUser } from '@renderer/chat/store'
import { AppWindow, AudioNote, DictationOrigin, ExposedContext, FocusedWindow } from '@highlight/common'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import { GlobalState } from '@highlight/ipc'
import { ChatMessage, ClipboardHistoryItem, ClipboardRecordType } from '@renderer/chat/types'
import { useRequestContext } from '@renderer/overlay/Assistant/useRequestContext.ts'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { showSignIn } from '@renderer/chat/utils/auth'
import ModelIpcRenderer from '@renderer/ipc/ModelIpc.renderer'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { v4 as uuidv4 } from 'uuid'
import AsrIpcRenderer from '@renderer/ipc/AsrIpc.renderer'
import BrowserSupportIpcRenderer from '@renderer/ipc/BrowserSupportIpc.renderer'
import { useEnableVoiceMode } from '@renderer/hooks/useEnableVoiceMode'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { useStopAudioNote } from '@renderer/hooks/useStopAudioNote'
import { useQueryClient } from '@tanstack/react-query'
import Markdown from 'react-markdown'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { breakCellComponents } from '@renderer/components/markdown-extras'
import remarkGfm from 'remark-gfm'
import ContextDbIpcRenderer from '@renderer/ipc/ContextDbIpc.renderer.ts'
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import { HighlightShellsProvider } from '@renderer/providers/highlight-shells-provider'
import { useFocusedWindowSnapshot } from '@renderer/hooks/use-focused-window'

const cleanMarkdown = (markdown: string) => {
  const rendererDiv = document.createElement('div')
  const root = createRoot(rendererDiv)
  const renderedMarkdown = Markdown({
    children: markdown,
    remarkPlugins: [remarkGfm],
    components: breakCellComponents,
  })

  // Force synchronous rendering
  flushSync(() => {
    root.render(renderedMarkdown)
  })

  const richHtmlTextFromElement = rendererDiv.innerHTML
  const textFromElement = rendererDiv.textContent
  return { richHtmlTextFromElement, textFromElement }
}

const ChatProviders: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { facts, aboutMeString } = useFacts()
  const requestContext = useRequestContext()

  // Speaking state management
  const [isSpeaking, setIsSpeaking] = useState('')
  const currentAudioRef = useRef<HTMLAudioElement | null>(null)

  // Auth
  const getAuthToken = useCallback(async () => AuthIpcRenderer.getAccessToken(), [])
  const handleUnauthorized = useCallback(
    async (rejectedToken?: string) => AuthIpcRenderer.handleUnauthorized(rejectedToken),
    [],
  )

  // Actions
  const copyTextToClipboard = useCallback(async (text: string, isMarkdown?: boolean) => {
    if (isMarkdown) {
      const { richHtmlTextFromElement, textFromElement } = cleanMarkdown(text)
      OverlayIpcRenderer.writeTextToClipboard(textFromElement || '', richHtmlTextFromElement || '')
    } else {
      OverlayIpcRenderer.writeTextToClipboard(text)
    }
  }, [])

  const compressBase64Image = useCallback(
    async (base64Image: string, options: { format: 'png' | 'jpg'; quality: number }) => {
      if (options.format === 'png') {
        return OverlayIpcRenderer.compressBase64ImageToPng({
          base64Image,
          quality: options.quality,
        })
      } else if (options.format === 'jpg') {
        return OverlayIpcRenderer.compressBase64ImageToJpeg({
          base64Image,
          quality: options.quality,
        })
      }
      return base64Image
    },
    [],
  )

  const getAboutMe = useCallback(async () => {
    if (facts.length === 0 && aboutMeString.length === 0) {
      return ''
    }
    return facts.join('\n') + '\n' + aboutMeString
  }, [facts, aboutMeString])

  const getAppIcon = useCallback(async (appName: string) => {
    return OverlayIpcRenderer.getAppIcon(appName)
  }, [])

  const getClipboard = useCallback(async () => {
    return OverlayIpcRenderer.getClipboardContents()
  }, [])

  const getClipboardHistory = useCallback(
    async (options?: { query?: string; type?: ClipboardRecordType; limit?: number }) => {
      const { query, type, limit } = options ?? {}
      const history = query?.length
        ? await ContextDbIpcRenderer.searchClipboardHistory({
            query: query,
            type: type,
          })
        : await ContextDbIpcRenderer.getClipboardHistory({
            limit: limit ?? 25,
            type: type,
          })
      return await Promise.all<ClipboardHistoryItem>(
        history.map((item) => {
          return new Promise(async (resolve) => {
            resolve({
              ...item,
              app_icon: item.app_name ? await OverlayIpcRenderer.getAppIcon(item.app_name) : undefined,
            } as ClipboardHistoryItem)
          })
        }),
      )
    },
    [],
  )

  const deleteClipboardItem = useCallback(async (id: number) => {
    return ContextDbIpcRenderer.deleteClipboardItem(id)
  }, [])

  const getConversation = useCallback(async (id: string) => {
    return AudioNoteIpcRenderer.getAudioNote(id)
  }, [])

  const getConversations = useCallback(async (options?: { timeWindowDays?: number }) => {
    if (options?.timeWindowDays !== undefined) {
      return AudioNoteIpcRenderer.getAudioNotesWithinTimeWindowDays(options.timeWindowDays)
    }
    return AudioNoteIpcRenderer.getAudioNotes()
  }, [])

  const getScreenshotSources = useCallback(async (includeAllWindows?: boolean) => {
    const windows = await OverlayIpcRenderer.getScreenshotWindows(includeAllWindows)
    const displays = await OverlayIpcRenderer.getDisplayScreenshots()
    return {
      windows,
      displays,
    }
  }, [])

  const getWindowScreenshot = useCallback((windowTitle: string) => {
    return OverlayIpcRenderer.screenshotWindow(windowTitle)
  }, [])

  const focusWindow = useCallback(async (windowTitle: string, pid?: number) => {
    await OverlayIpcRenderer.focusWindow({ title: windowTitle, pid })
  }, [])

  const handleTyping = useCallback(
    async (message: ChatMessage, lastFocusedWindow?: FocusedWindow, options?: { source?: string }) => {
      if (!lastFocusedWindow) {
        return
      }
      GlobalState.set('typing.cancelled', false)
      AmplitudeRenderer.trackEvent('Type', {
        source: options?.source ?? 'unknown',
        app: lastFocusedWindow?.appName,
      })
      try {
        OverlayIpcRenderer.toggleWindowClickThrough(true)
        await OverlayIpcRenderer.focusWindow({
          title: lastFocusedWindow?.windowTitle ?? '',
          pid: lastFocusedWindow?.pid,
        })
        if (options?.source === 'assistant_message') {
          await new Promise((resolve) => setTimeout(resolve, 50))
        }
        const { richHtmlTextFromElement, textFromElement } = cleanMarkdown(message.content as string)

        const hasCodeBlock = richHtmlTextFromElement?.includes('<code')
        const textToType =
          (!hasCodeBlock && textFromElement) ||
          (hasCodeBlock
            ? // @ts-ignore @todo fix this type! make sure it's string before trying this
              message.content.replace(/```(.*\n)?/g, '').trim()
            : (message.content as string).trim())

        await OverlayIpcRenderer.typeText({
          text: textToType,
          pid: lastFocusedWindow?.pid,
          method: 'paste',
          html: !hasCodeBlock ? richHtmlTextFromElement : undefined,
        })
      } catch (err) {
        console.error(err)
      } finally {
        OverlayIpcRenderer.toggleWindowClickThrough(false)
      }
    },
    [],
  )

  const requestCurrentContext = useCallback(async () => {
    const highlightContext = await requestContext('continuous-context')
    if (!highlightContext) {
      return null
    }

    const exposedContext: ExposedContext = {
      ...highlightContext,
      attachments: highlightContext.environment.imageDataBase64 // @todo deprecate + remove - Josh
        ? [
            {
              type: 'screenshot',
              value: `data:image/png;base64,${highlightContext.environment.imageDataBase64}`,
            },
          ]
        : [],
      windowScope: 'visible', // @todo deprecate + remove - Josh
    }

    // if (!chatContextHistory?.length) {
    //   return exposedContext
    // }

    return exposedContext
  }, [])

  const requestContextForWindow = useCallback(async (window: AppWindow) => {
    return OverlayIpcRenderer.requestContextForWindow({ window })
  }, [])

  const getActiveDisplayScreenshot = useCallback(async () => {
    return OverlayIpcRenderer.getActiveDisplayScreenshot()
  }, [])

  const trackEvent = useCallback((eventName: string, eventProps?: object) => {
    AmplitudeRenderer.trackEvent(eventName, {
      ...eventProps,
      origin: 'assistant',
    })
  }, [])

  const getModels = useCallback(async () => {
    return await ModelIpcRenderer.fetchModels()
  }, [])

  const onLogin = useCallback((screenHint?: 'sign-in' | 'sign-up') => {
    showSignIn(screenHint)
  }, [])

  const { startAudioNote } = useStartAudioNote()
  const stopAudioNote = useStopAudioNote()
  const enableVoiceMode = useEnableVoiceMode()

  // Speaking functionality with state management
  const stopSpeaking = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.onended = () => {}
      currentAudioRef.current.onerror = () => {}
      currentAudioRef.current.pause()
      currentAudioRef.current.src = ''
      currentAudioRef.current = null
    }
    setIsSpeaking('')
  }, [])

  const handleSpeaking = useCallback(
    async (text: string, id: string) => {
      try {
        // Stop current audio if playing
        if (currentAudioRef.current || isSpeaking === id) {
          stopSpeaking()
          // If the message currently being spoken is the same, just stop it and return
          if (isSpeaking === id) {
            return
          }
        }

        // Immediately set speaking to true to prevent race conditions from rapid clicking
        setIsSpeaking(id)
        const audio = new Audio()
        const audioId = uuidv4()
        audio.id = audioId
        currentAudioRef.current = audio
        const { textFromElement } = cleanMarkdown(text)
        text = textFromElement || text

        // Call the speak endpoint
        const authToken = await getAuthToken()
        const response = await fetch(getChatApiRoute('api/v1/speak'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            text: text.trim(),
            language: 'en-US',
            gender: 'female',
            outputFormat: 'mp3',
          }),
        })

        if (!response.ok) {
          throw new Error(`Failed to generate speech: ${response.statusText}`)
        }

        const audioBlob = await response.blob()
        const audioUrl = URL.createObjectURL(audioBlob)
        if (!currentAudioRef.current || currentAudioRef.current.id !== audioId) {
          // would happen if cancelled
          return
        }

        audio.src = audioUrl

        audio.onended = () => {
          setIsSpeaking('')
          currentAudioRef.current = null
          URL.revokeObjectURL(audioUrl)
        }

        audio.onerror = (event) => {
          console.error('Audio element issue. Error playing audio for id', id, event)
        }

        await audio.play()
      } catch (error) {
        console.error('Error generating speech:', error)
        setIsSpeaking('')
        currentAudioRef.current = null
      }
    },
    [isSpeaking, stopSpeaking, getAuthToken],
  )

  const startDictation = useCallback(async (callOrigin: DictationOrigin) => {
    AsrIpcRenderer.startDictation(callOrigin)
  }, [])

  const stopDictation = useCallback(async (callOrigin: DictationOrigin, cancelled: boolean = false) => {
    AsrIpcRenderer.stopDictation({ source: 0, callOrigin, cancelled })
  }, [])

  const getBrowserTabs = useCallback(async () => {
    return await BrowserSupportIpcRenderer.getTabs()
  }, [])

  const getBrowserTabContext = useCallback(async (tabId: number) => {
    return await BrowserSupportIpcRenderer.getBrowserTabContext(tabId)
  }, [])

  const startChatAudioNote = useCallback(async () => {
    startAudioNote({ source: 'assistant' })
  }, [startAudioNote])

  const getAudioNotes = useCallback(async () => {
    const conversations = await AudioNoteIpcRenderer.getAudioNotes()
    return conversations as Array<AudioNote>
  }, [])

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause()
        currentAudioRef.current.src = ''
      }
    }
  }, [])

  return (
    <AuthProvider getAuthToken={getAuthToken} handleUnauthorized={handleUnauthorized}>
      <ReactQueryProvider>
        <QueryInvalidationHandler />
        <RuntimeHandler />
        <ActionsProvider
          compressBase64Image={compressBase64Image}
          copyTextToClipboard={copyTextToClipboard}
          getAboutMe={getAboutMe}
          getAppIcon={getAppIcon}
          getClipboard={getClipboard}
          getClipboardHistory={getClipboardHistory}
          deleteClipboardItem={deleteClipboardItem}
          getConversation={getConversation}
          getConversations={getConversations}
          getScreenshotSources={getScreenshotSources}
          getWindowScreenshot={getWindowScreenshot}
          getActiveDisplayScreenshot={getActiveDisplayScreenshot}
          handleTyping={handleTyping}
          focusWindow={focusWindow}
          requestCurrentContext={requestCurrentContext}
          requestContextForWindow={requestContextForWindow}
          trackEvent={trackEvent}
          onLogin={onLogin}
          getModels={getModels}
          getAudioNotes={getAudioNotes}
          startAudioNote={startChatAudioNote}
          stopAudioNote={stopAudioNote}
          enableVoiceMode={enableVoiceMode}
          handleSpeaking={handleSpeaking}
          isSpeaking={isSpeaking}
          stopSpeaking={stopSpeaking}
          startDictation={startDictation}
          stopDictation={stopDictation}
          getBrowserTabs={getBrowserTabs}
          getBrowserTabContext={getBrowserTabContext}
        >
          <BaseTooltip.Provider>
            <ModelProvider>
              <HighlightShellsProvider>{children}</HighlightShellsProvider>
            </ModelProvider>
          </BaseTooltip.Provider>
        </ActionsProvider>
      </ReactQueryProvider>
    </AuthProvider>
  )
}

export default memo(ChatProviders)

// Handle query invalidation on auth events
const QueryInvalidationHandler: React.FC = memo(() => {
  const queryClient = useQueryClient()
  const user = useUser()
  const lastUserId = useRef<string | undefined>(user?.id)

  useEffect(() => {
    if (!user || lastUserId.current === user?.id) {
      return
    }

    // User ID changed
    lastUserId.current = user?.id

    if (user?.isAnonymous) {
      queryClient.clear()
      console.log('Cleared all query cache due to sign out')
    } else {
      queryClient.invalidateQueries()
      console.log('Invalidated user queries due to sign in')
    }
  }, [queryClient, user])

  return null
})

QueryInvalidationHandler.displayName = 'QueryInvalidationHandler'

// Handle runtime changes for the chat store
const RuntimeHandler: React.FC = memo(() => {
  const { focusedWindow, appIcon } = useFocusedWindowSnapshot()
  const setLastFocusedWindow = useSetLastFocusedWindow()

  useEffect(() => {
    setLastFocusedWindow(focusedWindow ? { ...focusedWindow, appIcon } : undefined)
  }, [appIcon, focusedWindow, setLastFocusedWindow])

  return null
})

RuntimeHandler.displayName = 'RuntimeHandler'
